import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Row, createColumnHelper } from "@tanstack/react-table";
import {
  defaultDateRange,
  useAuthConfig,
  useEndpointTagsQuery,
} from "@theprelude/core";
import { Button, TagIcon, TrashcanIcon } from "@theprelude/ds";
import {
  DeleteEndpointModal,
  useDeleteEndpointModal,
} from "@/components/modal/delete-endpoint-modal";
import {
  checkboxColumn,
  platformColumn,
  edrColumn,
  endpointIdColumn,
  hostColumn,
  lastSeenColumn,
  osColumn,
  policyColumn,
  protectedColumn,
  tagsColumn,
  defaultHidden,
  getColumnVisibility,
} from "@/components/table/columns";
import { useTable } from "@/components/table/hooks";
import SelectionBar from "@/components/table/selection-bar";
import { VirtualTable } from "@/components/table/virtual-table";
import TagOverlay, {
  useTagOverlayProps,
} from "@/components/tag-overlay/tag-overlay";
import {
  DashboardEndpoint,
  dashboardEndpointsQuery,
} from "@/hooks/quickview-queries";
import { ensureAuth } from "@/lib/auth";
import { authorizeFeatures } from "@/lib/authorize-feature";
import { getActivityQuery } from "@/lib/validators";

export const Route = createFileRoute("/_app/dashboard/endpoints")({
  loaderDeps: () => {
    return { query: getActivityQuery() };
  },
  beforeLoad: async (opts) => {
    await authorizeFeatures(opts.context, ["detect"]);
  },
  loader: async (opts) => {
    return opts.context.queryClient.ensureQueryData(
      dashboardEndpointsQuery(
        await ensureAuth(opts.context.auth),
        opts.deps.query,
      ),
    );
  },
  component: EndpointsPage,
});

const columnHelper = createColumnHelper<DashboardEndpoint>();

const columns = [
  columnHelper.display(checkboxColumn),
  columnHelper.accessor("endpoint_id", defaultHidden(endpointIdColumn)),
  columnHelper.accessor("host", hostColumn),
  columnHelper.accessor("dos", platformColumn),
  columnHelper.accessor("os", osColumn),
  columnHelper.accessor("control", edrColumn),
  columnHelper.accessor("last_seen", lastSeenColumn),
  columnHelper.accessor("tags", tagsColumn),
  columnHelper.accessor("policy", policyColumn),
  columnHelper.accessor("performance", protectedColumn),
];

function EndpointsPage() {
  const query = {
    start: defaultDateRange.start.toISOString(),
    finish: defaultDateRange.finish.toISOString(),
  };
  const authConfig = useAuthConfig();
  const endpointsTable = useSuspenseQuery(
    dashboardEndpointsQuery(authConfig, query),
  );
  const setDeleteEndpointModal = useDeleteEndpointModal(
    (state) => state.setValue,
  );

  const table = useTable({
    name: "dashboard-endpoints",
    columns,
    data: endpointsTable.data ?? [],
    initialState: {
      columnVisibility: getColumnVisibility(columns),
    },
    getRowId: (row) => row.endpoint_id,
  });

  const selectedRows = table.getSelectedRowModel().rows;

  const endpointIds = selectedRows.map((r) => r.id);
  const { data: tags } = useEndpointTagsQuery();

  const showSelectionBar = selectedRows.length > 0;
  const setTagOverlayProps = useTagOverlayProps((state) => state.setValue);

  function handleTagOverlayProps(
    endpoints: string[],
    tags: string[],
    selectedRows: Row<DashboardEndpoint>[],
  ) {
    const relevantTags = [
      ...new Set(selectedRows.flatMap((r) => r.original.tags ?? [])),
    ];

    const rowTags = selectedRows.map((r) => r.original.tags);
    const sharedTags =
      rowTags.reduce((acc, current) => {
        return acc?.filter((t) => current?.includes(t));
      }, rowTags[0] ?? []) ?? [];

    setTagOverlayProps({
      endpoints,
      tags,
      relevantTags,
      sharedTags,
    });
  }

  return (
    <>
      <VirtualTable withActionBar showFilter table={table} />
      <TagOverlay resetTable={() => table.resetRowSelection()} />
      {showSelectionBar && (
        <SelectionBar>
          <Button intent="secondary" onClick={() => table.resetRowSelection()}>
            Cancel
          </Button>
          <Button
            intent="secondary"
            icon={<TagIcon />}
            onClick={() =>
              handleTagOverlayProps(endpointIds, tags ?? [], selectedRows)
            }
          >
            Tag endpoints
          </Button>
          <Button
            intent="negative"
            icon={<TrashcanIcon />}
            onClick={() => setDeleteEndpointModal(true)}
          >
            Delete endpoints
          </Button>
        </SelectionBar>
      )}
      <DeleteEndpointModal endpointIds={endpointIds} />
    </>
  );
}
